import {
  Add,
  CheckCircle,
  Close,
  DeleteOutline,
  OpenInNew,
  Search,
  Share,
  Storefront,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAuth } from 'hooks/useAuth';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shareOrCopyLink } from 'utils/share';
import { getPublicListUrl, getShareListUrl } from 'utils/shareUrl';
import { useAdminListaPresentesRules } from './AdminListaPresentesPage.rules';

const formatCurrency = (valor: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

const AdminListaPresentesPage: React.FC = () => {
  const { listaId } = useParams<{ listaId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuestUser = user?.authProvider === 'guest';

  const [linkCopiado, setLinkCopiado] = React.useState(false);

  const {
    lista,
    presentes,
    presentesFiltrados,
    loading,
    error,
    setError,
    success,
    dialogOpen,
    dialogMode,
    form,
    saving,
    presentesLoadingId,
    catalogoOpen,
    catalogoLoading,
    catalogoProdutos,
    catalogoError,
    catalogoAdicionandoId,
    catalogoSuccess,
    catalogoCategorias,
    catalogoFiltroCategoria,
    catalogoSelecionados,
    catalogoPage,
    setCatalogoPage,
    catalogoTotalPages,
    catalogoFaixaPrecoLabel,
    catalogoResumoPaginacao,
    catalogoFaixaPrecoAtivaChave,
    filtrosPresentes,
    categoriasDisponiveis,
    precoChips,
    faixaPrecoAtivaChave,
    tituloLista,
    presentesAfiliadosIds,
    fecharDialog,
    handleFormChange,
    salvarPresente,
    abrirCatalogoAfiliado,
    fecharCatalogoAfiliado,
    handleCatalogoBuscar,
    handleCatalogoFiltroCategoriaChange,
    handleCatalogoFaixaPrecoChange,
    handleCatalogoKeyDown,
    handleCatalogoInputChange,
    handleSelecionarProduto,
    aplicarCategoriaRapida,
    aplicarFaixaPrecoRapida,
    removerPresente,
  } = useAdminListaPresentesRules(listaId);

  const handleCopiarLink = React.useCallback(
    async (slug: string) => {
      const url = getShareListUrl(slug);
      const normalizedTitle = (tituloLista || '').trim();
      const shareTitle = normalizedTitle
        ? `Veja minha lista: ${normalizedTitle}`
        : 'Veja minha lista de presentes';
      const shareText = normalizedTitle
        ? `Fiz minha lista "${normalizedTitle}". Vem ver!`
        : 'Fiz minha lista de presentes. Vem ver!';

      const outcome = await shareOrCopyLink({
        url,
        title: shareTitle,
        text: shareText,
      });

      if (outcome !== 'cancelled') {
        setLinkCopiado(true);
        setTimeout(() => setLinkCopiado(false), 2000);
      }
    },
    [tituloLista]
  );

  const handleCloseAlert = () => setError('');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !lista) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Gerenciar presentes</Typography>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Stack>
        <Alert severity="error" onClose={handleCloseAlert}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {isGuestUser && (
        <Alert
          severity="info"
          sx={{ mt: -3, mb: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.70rem' } }}
          icon={false}
        >
          Você está como convidado — lista salva. Para não perdê-la, toque no ícone do perfil e
          vincule um e-mail.
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        useFlexGap
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {tituloLista || 'Presentes da lista'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0}>
          {lista?.slug && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNew />}
                onClick={() =>
                  window.open(getPublicListUrl(lista.slug), '_blank', 'noopener,noreferrer')
                }
              >
                Visualizar
              </Button>
              <Button
                size="small"
                variant="outlined"
                color={linkCopiado ? 'success' : 'inherit'}
                startIcon={<Share />}
                onClick={() => handleCopiarLink(lista.slug)}
              >
                {linkCopiado ? 'Pronto!' : 'Compartilhar'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={handleCloseAlert}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {(categoriasDisponiveis.length > 0 || precoChips.length > 0) && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {categoriasDisponiveis.length > 0 && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, flex: 1 }}>
                  <InputLabel id="admin-lista-presentes-categoria-label" shrink>
                    Categoria
                  </InputLabel>
                  <Select
                    id="admin-lista-presentes-categoria"
                    labelId="admin-lista-presentes-categoria-label"
                    label="Categoria"
                    displayEmpty
                    value={filtrosPresentes.categoria}
                    onChange={(e) => aplicarCategoriaRapida(e.target.value as string)}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {categoriasDisponiveis.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {precoChips.length > 0 && (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, flex: 1 }}>
                  <InputLabel id="admin-lista-presentes-faixa-label" shrink>
                    Faixa de preço
                  </InputLabel>
                  <Select
                    id="admin-lista-presentes-faixa"
                    labelId="admin-lista-presentes-faixa-label"
                    label="Faixa de preço"
                    value={faixaPrecoAtivaChave === '|' ? '' : faixaPrecoAtivaChave}
                    displayEmpty
                    onChange={(e) => {
                      const val = e.target.value as string;
                      if (!val) {
                        aplicarFaixaPrecoRapida(undefined, undefined);
                      } else {
                        const chip = precoChips.find((c) => {
                          const mk = typeof c.min === 'number' ? c.min.toFixed(2) : '';
                          const xk = typeof c.max === 'number' ? c.max.toFixed(2) : '';
                          return `${mk}|${xk}` === val;
                        });
                        if (chip) aplicarFaixaPrecoRapida(chip.min, chip.max);
                      }
                    }}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {precoChips.map((chip) => {
                      const mk = typeof chip.min === 'number' ? chip.min.toFixed(2) : '';
                      const xk = typeof chip.max === 'number' ? chip.max.toFixed(2) : '';
                      return (
                        <MenuItem key={`${mk}|${xk}`} value={`${mk}|${xk}`}>
                          {chip.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button size="small" variant="contained" onClick={abrirCatalogoAfiliado}>
          + Adicionar
        </Button>
      </Box>

      {presentes.length === 0 ? (
        <Alert severity="info">
          Nenhum presente cadastrado ainda. Clique em "Novo presente" para adicionar a partir do
          catálogo.
        </Alert>
      ) : presentesFiltrados.length === 0 ? (
        <Alert severity="info">Nenhum presente corresponde aos filtros aplicados.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {presentesFiltrados.map((presente) => {
            const transacoes = presente.transacoesConfirmadas ?? 0;
            const nomeCurto =
              presente.nome.length > 80 ? `${presente.nome.slice(0, 80).trim()}` : presente.nome;
            const descricaoDerivada =
              presente.descricao && presente.descricao.trim().length > 0
                ? presente.descricao
                : presente.nome.length > 80
                  ? presente.nome.slice(80).trim() || presente.nome
                  : 'Sem descrição cadastrada.';
            const imagemPresente = presente.imagemUrl || presente.produtoAfiliado?.imagemUrl;
            const parceiro = presente.produtoAfiliado?.parceiro;

            return (
              <Card
                key={presente.id}
                variant="outlined"
                sx={{ height: '100%', overflow: 'visible' }}
              >
                <CardContent sx={{ p: 1.5, overflow: 'visible' }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        position: 'relative',
                        width: 88,
                        height: 88,
                        flexShrink: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'visible',
                        bgcolor: 'grey.50',
                        '&:hover .hover-zoom-overlay': {
                          opacity: 1,
                          transform: 'translate(-6px, -6px)',
                        },
                      }}
                    >
                      {imagemPresente ? (
                        <>
                          <Box
                            component="img"
                            src={imagemPresente}
                            alt={presente.nome}
                            className="hover-zoom"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Box
                            className="hover-zoom-overlay"
                            sx={{
                              position: 'absolute',
                              top: -16,
                              left: -16,
                              width: 250,
                              height: 250,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'white',
                              borderRadius: 2,
                              boxShadow: 8,
                              opacity: 0,
                              pointerEvents: 'none',
                              transform: 'translate(0px, 0px)',
                              transition: 'opacity 0.16s ease, transform 0.16s ease',
                              zIndex: 8,
                            }}
                          >
                            <Box
                              component="img"
                              src={imagemPresente}
                              alt={presente.nome}
                              sx={{ width: '96%', height: '96%', objectFit: 'contain' }}
                            />
                          </Box>
                        </>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.disabled',
                          }}
                        >
                          <Storefront />
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={0.75} flexGrow={1} minWidth={0}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {nomeCurto}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mt: 0.25,
                            }}
                          >
                            {descricaoDerivada}
                          </Typography>
                        </Box>
                        <Stack spacing={0.5} alignItems="flex-end" flexShrink={0} ml={1}>
                          {presentesLoadingId === presente.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Tooltip title="Remover">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removerPresente(presente)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Typography variant="subtitle1" color="primary" fontWeight={700}>
                            {formatCurrency(presente.valor)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                        {parceiro && (
                          <Chip label={parceiro} size="small" variant="outlined" color="info" />
                        )}
                        {presente.categoria && (
                          <Chip label={presente.categoria} size="small" variant="outlined" />
                        )}
                        {presente.origem === 'afiliado' && (
                          <Chip label="Catálogo" size="small" variant="outlined" color="primary" />
                        )}
                        {transacoes > 0 && (
                          <Chip
                            label={`Recebido ${transacoes}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={fecharDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialogMode === 'create' ? 'Adicionar presente' : 'Editar presente'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Nome do presente"
              value={form.nome}
              onChange={(event) => handleFormChange('nome', event.target.value)}
              autoFocus
              required
            />
            <TextField
              label="Descrição"
              value={form.descricao}
              onChange={(event) => handleFormChange('descricao', event.target.value)}
              multiline
              minRows={3}
              placeholder="Conte um pouco sobre este presente"
            />
            <TextField
              label="Valor"
              value={form.valor}
              onChange={(event) => handleFormChange('valor', event.target.value)}
              placeholder="Ex: 150,00"
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            />
            <TextField
              label="Categoria"
              value={form.categoria}
              onChange={(event) => handleFormChange('categoria', event.target.value)}
              placeholder="Ex: Experiências, Viagem, Casa"
            />
            <TextField
              label="URL da imagem"
              value={form.imagemUrl}
              onChange={(event) => handleFormChange('imagemUrl', event.target.value)}
              placeholder="https://..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={salvarPresente} variant="contained" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={catalogoOpen} onClose={fecharCatalogoAfiliado} fullWidth maxWidth="md">
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          Adicionar presente do catálogo afiliado
          <IconButton
            aria-label="Fechar"
            onClick={fecharCatalogoAfiliado}
            disabled={catalogoAdicionandoId !== null}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                fullWidth
                label="Buscar produto"
                type="search"
                onChange={(event) => handleCatalogoInputChange(event.target.value)}
                onKeyDown={handleCatalogoKeyDown}
                placeholder="Ex: panelas, jogo de lençol, eletrodomésticos"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  inputProps: { 'aria-label': 'Buscar produto no catálogo' },
                }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleCatalogoBuscar}
                disabled={catalogoLoading}
              >
                {catalogoLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            </Stack>
            {(catalogoCategorias.length > 0 || precoChips.length > 0) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {catalogoCategorias.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 170, flex: 1 }}>
                    <InputLabel shrink>Categoria</InputLabel>
                    <Select
                      label="Categoria"
                      displayEmpty
                      value={catalogoFiltroCategoria}
                      onChange={(e) =>
                        handleCatalogoFiltroCategoriaChange((e.target.value as string) || '')
                      }
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {catalogoCategorias.map((categoria) => (
                        <MenuItem key={categoria} value={categoria}>
                          {categoria}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {precoChips.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 190, flex: 1 }}>
                    <InputLabel shrink>Faixa de preço</InputLabel>
                    <Select
                      label="Faixa de preço"
                      value={
                        catalogoFaixaPrecoAtivaChave === '|' ? '' : catalogoFaixaPrecoAtivaChave
                      }
                      displayEmpty
                      onChange={(e) => {
                        const val = e.target.value as string;
                        if (!val) {
                          handleCatalogoFaixaPrecoChange(undefined, undefined);
                          return;
                        }
                        const chip = precoChips.find((c) => {
                          const minKey = typeof c.min === 'number' ? c.min.toFixed(2) : '';
                          const maxKey = typeof c.max === 'number' ? c.max.toFixed(2) : '';
                          return `${minKey}|${maxKey}` === val;
                        });
                        if (chip) handleCatalogoFaixaPrecoChange(chip.min, chip.max);
                      }}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {precoChips.map((chip) => {
                        const minKey = typeof chip.min === 'number' ? chip.min.toFixed(2) : '';
                        const maxKey = typeof chip.max === 'number' ? chip.max.toFixed(2) : '';
                        return (
                          <MenuItem key={`${minKey}|${maxKey}`} value={`${minKey}|${maxKey}`}>
                            {chip.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            )}
            {catalogoSuccess && <Alert severity="success">{catalogoSuccess}</Alert>}
            {catalogoError && <Alert severity="error">{catalogoError}</Alert>}
            {(catalogoResumoPaginacao || catalogoFaixaPrecoLabel) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.5, sm: 1.5 }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                {catalogoResumoPaginacao && (
                  <Typography variant="body2" color="text.secondary">
                    {catalogoResumoPaginacao}
                  </Typography>
                )}
                {catalogoFaixaPrecoLabel && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: { xs: 'left', sm: 'right' } }}
                  >
                    {catalogoFaixaPrecoLabel}
                  </Typography>
                )}
              </Stack>
            )}
            {catalogoLoading && catalogoProdutos.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {catalogoLoading && catalogoProdutos.length > 0 && (
                  <Box display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress size={24} />
                  </Box>
                )}
                {catalogoProdutos.length === 0 ? (
                  <Alert severity="info">
                    Nenhum produto encontrado. Ajuste os filtros ou tente outro termo.
                  </Alert>
                ) : (
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
                    gap={2}
                  >
                    {catalogoProdutos
                      .filter((produto) => !presentesAfiliadosIds.has(produto.id))
                      .map((produto) => {
                        const jaNaLista = false;
                        const selecionado = catalogoSelecionados.has(produto.id);
                        const adicionando = catalogoAdicionandoId === produto.id;
                        const bloqueado = Boolean(
                          catalogoAdicionandoId && catalogoAdicionandoId !== produto.id
                        );
                        const tagsVisiveis = produto.tags?.slice(0, 3) ?? [];

                        return (
                          <Card
                            key={produto.id}
                            variant="outlined"
                            sx={{
                              height: '100%',
                              position: 'relative',
                              display: 'flex',
                              flexDirection: 'column',
                              borderColor: selecionado ? 'primary.main' : 'divider',
                              boxShadow: selecionado ? 4 : 0,
                              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                              opacity: adicionando ? 0.8 : 1,
                            }}
                          >
                            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                              <Checkbox
                                color="primary"
                                checked={selecionado}
                                disabled={selecionado || bloqueado || adicionando}
                                onChange={() => {
                                  if (!selecionado && !bloqueado && !adicionando) {
                                    void handleSelecionarProduto(produto);
                                  }
                                }}
                                inputProps={{
                                  'aria-label': selecionado
                                    ? 'Produto já adicionado'
                                    : 'Selecionar produto para adicionar',
                                }}
                              />
                            </Box>
                            <CardContent
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                pb: 2,
                                flexGrow: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  height: 140,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'grey.100',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                }}
                              >
                                {produto.imagemUrl ? (
                                  <Box
                                    component="img"
                                    src={produto.imagemUrl}
                                    alt={produto.nome}
                                    sx={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                ) : (
                                  <Storefront color="disabled" fontSize="large" />
                                )}
                              </Box>
                              <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  minHeight: 48,
                                }}
                              >
                                {produto.nome}
                              </Typography>
                              {produto.parceiro && (
                                <Typography variant="body2" color="text.secondary">
                                  Parceiro: {produto.parceiro}
                                </Typography>
                              )}
                              {produto.descricao && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {produto.descricao}
                                </Typography>
                              )}
                              <Typography variant="subtitle1" color="primary">
                                {formatCurrency(produto.preco)}
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {produto.categoria && (
                                  <Chip label={produto.categoria} size="small" variant="outlined" />
                                )}
                                {tagsVisiveis.map((tag) => (
                                  <Chip key={`${produto.id}-${tag}`} label={tag} size="small" />
                                ))}
                              </Stack>
                              {selecionado && (
                                <Chip
                                  icon={<CheckCircle fontSize="small" />}
                                  label={jaNaLista ? 'Já na lista' : 'Adicionado'}
                                  color="success"
                                  size="small"
                                  variant={jaNaLista ? 'filled' : 'outlined'}
                                  sx={{ alignSelf: 'flex-start' }}
                                />
                              )}
                            </CardContent>
                            {!selecionado && (
                              <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                                <Button
                                  fullWidth
                                  size="small"
                                  variant="contained"
                                  startIcon={<Add fontSize="small" />}
                                  onClick={() => {
                                    void handleSelecionarProduto(produto);
                                  }}
                                  disabled={bloqueado || adicionando}
                                >
                                  {adicionando ? 'Adicionando...' : 'Adicionar'}
                                </Button>
                              </CardActions>
                            )}
                          </Card>
                        );
                      })}
                  </Box>
                )}
                {catalogoProdutos.length > 0 && catalogoTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={catalogoTotalPages}
                      page={catalogoPage}
                      onChange={(_, value) => setCatalogoPage(value)}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      disabled={catalogoLoading}
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharCatalogoAfiliado} disabled={catalogoAdicionandoId !== null}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminListaPresentesPage;
